import { formatTimeAgo, resolveIndustryLabel } from "@/lib/utils";
import { getLastActivityDate } from "@/services/deals-service";
import { usePortcoStore } from "@/store/portco-store";
import { useWorkspaceStore } from "@/store/workspace-store";
import { ArrowDown, ArrowUp, ArrowUpDown, Building2, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Input } from "../ui/input";
import { Skeleton } from "../ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { CompanyAvatar } from "./company-avatar";
import { getFitScore, hasFitScore } from "./utils";

type SortKey = "name" | "fitScore" | "lastActivity";

export default function PortcoComponent() {
  const { deals, isLoading, fetchDeals } = usePortcoStore();
  const { activeWorkspaceId } = useWorkspaceStore();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: SortKey;
    direction: "asc" | "desc";
  }>({ key: "lastActivity", direction: "desc" });

  // Fetch deals when component mounts or workspace changes
  useEffect(() => {
    if (activeWorkspaceId) {
      fetchDeals(activeWorkspaceId);
    }
  }, [activeWorkspaceId, fetchDeals]);

  if (isLoading) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="shrink-0">
          <div className="flex h-16 items-end border-b border-border px-6 pb-3">
            <Skeleton className="h-8 w-44" />
          </div>
          <div className="px-6 py-2.5">
            <Skeleton className="h-8 w-full md:w-96" />
          </div>
        </div>
        <div className="flex-1 overflow-hidden p-4">
          <div className="flex h-full flex-col overflow-hidden rounded-md border border-border bg-card">
            <div className="border-b border-border px-4 py-3">
              <Skeleton className="h-3 w-40" />
            </div>
            {Array.from({ length: 8 }).map((_, rowIdx) => (
              <div
                key={rowIdx}
                className="flex items-center gap-4 border-b border-border/40 px-4 py-3.5 last:border-b-0"
              >
                <Skeleton className="h-8 w-8 rounded-full" />
                <Skeleton className="h-4 w-40" />
                <Skeleton className="ml-auto h-4 w-12" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Filter portfolio companies and apply search
  const allPortco = Object.values(deals).filter(
    (deal) => deal.overview.status === "Portfolio Companies"
  );
  const portcoDeals = allPortco.filter(
    (deal) =>
      searchQuery === "" ||
      deal.overview.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSort = (key: SortKey) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedDeals = [...portcoDeals].sort((a, b) => {
    if (sortConfig.key === "name") {
      return sortConfig.direction === "asc"
        ? a.overview.name.localeCompare(b.overview.name)
        : b.overview.name.localeCompare(a.overview.name);
    }

    if (sortConfig.key === "fitScore") {
      // Un-analyzed deals (no Fit Score) sort to the bottom.
      const fa = hasFitScore(a) ? (getFitScore(a) as number) : -1;
      const fb = hasFitScore(b) ? (getFitScore(b) as number) : -1;
      return sortConfig.direction === "asc" ? fa - fb : fb - fa;
    }

    // lastActivity
    const dateA = getLastActivityDate(a).getTime();
    const dateB = getLastActivityDate(b).getTime();
    return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
  });

  const SortIcon = ({ column }: { column: SortKey }) => {
    if (sortConfig.key !== column) {
      return (
        <ArrowUpDown className="ml-1.5 inline h-3 w-3 text-muted-foreground/40" />
      );
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp className="ml-1.5 inline h-3 w-3 text-foreground" />
    ) : (
      <ArrowDown className="ml-1.5 inline h-3 w-3 text-foreground" />
    );
  };

  // Fit Score cell — mirrors the Pipeline: score when analyzed, otherwise a
  // "Run analysis →" link to the deal's Analysis tab.
  const renderFitScore = (deal: (typeof portcoDeals)[number]) =>
    hasFitScore(deal) ? (
      <span className="font-mono text-sm font-medium tabular-nums text-foreground">
        {Math.round(getFitScore(deal) as number)}
        <span className="ml-0.5 font-normal text-muted-foreground/50">/100</span>
      </span>
    ) : (
      <Link
        to={`/${activeWorkspaceId}/deals/${deal.id}?tab=analysis`}
        className="whitespace-nowrap text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
      >
        Run analysis →
      </Link>
    );

  const renderIndustry = (deal: (typeof portcoDeals)[number]) => {
    if (!deal.overview.industry || deal.overview.industry.length === 0) {
      return <span className="text-xs text-muted-foreground/40">—</span>;
    }
    // Resolve GICS codes → labels, de-dupe, drop "—" placeholders.
    const labels = Array.from(
      new Set(
        deal.overview.industry
          .map(resolveIndustryLabel)
          .filter((l) => l && l !== "—")
      )
    );
    if (labels.length === 0) {
      return <span className="text-xs text-muted-foreground/40">—</span>;
    }
    return (
      <>
        {labels.slice(0, 2).map((label) => (
          <span
            key={label}
            className="inline-flex items-center rounded-sm bg-input px-1.5 py-0.5 text-xs text-foreground"
          >
            {label}
          </span>
        ))}
        {labels.length > 2 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex cursor-default items-center rounded-sm bg-input px-1.5 py-0.5 text-xs text-muted-foreground">
                  +{labels.length - 2}
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-[280px]">{labels.slice(2).join(", ")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </>
    );
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header — matches the Pipeline page (serif display title + search row) */}
      <div className="shrink-0">
        <div className="flex h-16 items-end border-b border-border px-6 pb-3">
          <h1 className="font-serif text-[32px] leading-[1.15] tracking-tight text-foreground">
            Portfolio
          </h1>
        </div>
        <div className="flex items-center gap-2 px-6 py-2.5">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search portfolio companies by name"
              className="h-8 w-full rounded-md border-border bg-background pl-9 pr-3 text-xs placeholder:text-muted-foreground/70 focus-visible:ring-1 focus-visible:ring-foreground/20 focus-visible:ring-offset-0"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden">
        {sortedDeals.length === 0 ? (
          <div className="flex h-full items-center justify-center p-4">
            <div className="flex flex-col items-center text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted">
                <Building2 className="h-7 w-7 text-muted-foreground" />
              </div>
              <h3 className="text-sm font-semibold text-foreground">
                {allPortco.length === 0
                  ? "No portfolio companies yet"
                  : "No matches found"}
              </h3>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                {allPortco.length === 0
                  ? 'Deals move here once their status is set to "Portfolio Companies".'
                  : "Try a different search term."}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop table — mirrors the Pipeline TableView container + columns */}
            <div className="hidden h-full w-full overflow-hidden p-4 md:block">
              <div className="flex h-full flex-col overflow-hidden rounded-md border border-border bg-card">
                <div className="flex-1 overflow-auto">
                  <Table className="w-full border-none outline-none">
                    <TableHeader className="sticky top-0 z-10 bg-card [&_tr]:border-b [&_tr]:border-border">
                      <TableRow className="border-b border-border [&_th]:whitespace-nowrap [&_th]:text-[10px] [&_th]:font-semibold [&_th]:uppercase [&_th]:tracking-widest [&_th]:text-muted-foreground/80">
                        <TableHead
                          onClick={() => handleSort("name")}
                          className="cursor-pointer px-6 transition-colors hover:text-foreground"
                        >
                          Company
                          <SortIcon column="name" />
                        </TableHead>
                        <TableHead
                          onClick={() => handleSort("fitScore")}
                          className="cursor-pointer transition-colors hover:text-foreground"
                        >
                          Fit Score
                          <SortIcon column="fitScore" />
                        </TableHead>
                        <TableHead className="hidden lg:table-cell">
                          Industry
                        </TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead
                          onClick={() => handleSort("lastActivity")}
                          className="hidden cursor-pointer transition-colors hover:text-foreground lg:table-cell"
                        >
                          Last Activity
                          <SortIcon column="lastActivity" />
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedDeals.map((deal) => (
                        <TableRow
                          key={deal.id}
                          className="group border-b border-border/40 transition-colors hover:bg-foreground/[0.02]"
                        >
                          <TableCell className="max-w-[300px] px-6 py-2">
                            <Link
                              to={`/${activeWorkspaceId}/deals/${deal.id}?tab=portco`}
                              className="flex items-center gap-2.5"
                            >
                              <CompanyAvatar
                                companyFiles={deal.company_files}
                                websiteUrl={deal.overview.website}
                                alt={deal.overview.name}
                              />
                              <span className="truncate text-sm font-medium text-foreground decoration-muted-foreground/30 underline-offset-2 group-hover:underline">
                                {deal.overview.name}
                              </span>
                            </Link>
                          </TableCell>
                          <TableCell>{renderFitScore(deal)}</TableCell>
                          <TableCell className="hidden px-4 py-2 lg:table-cell">
                            <div className="flex flex-wrap items-center gap-1">
                              {renderIndustry(deal)}
                            </div>
                          </TableCell>
                          <TableCell className="px-4 py-2">
                            <span className="text-xs text-muted-foreground/80">
                              {deal.overview.source || (
                                <span className="text-muted-foreground/40">
                                  —
                                </span>
                              )}
                            </span>
                          </TableCell>
                          <TableCell className="hidden px-4 py-2 lg:table-cell">
                            <span className="font-mono text-xs tabular-nums text-muted-foreground/60">
                              {formatTimeAgo(getLastActivityDate(deal))}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            {/* Mobile cards */}
            <div className="h-full space-y-3 overflow-auto p-4 md:hidden">
              {sortedDeals.map((deal) => (
                <div
                  key={deal.id}
                  className="space-y-3 rounded-md border border-border bg-card p-4 transition-colors hover:bg-foreground/[0.02]"
                >
                  <Link
                    to={`/${activeWorkspaceId}/deals/${deal.id}?tab=portco`}
                    className="group flex items-center gap-2.5 font-medium text-foreground hover:underline"
                  >
                    <CompanyAvatar
                      companyFiles={deal.company_files}
                      websiteUrl={deal.overview.website}
                      alt={deal.overview.name}
                    />
                    <span className="truncate">{deal.overview.name}</span>
                  </Link>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
                        Fit Score
                      </div>
                      {renderFitScore(deal)}
                    </div>
                    <div>
                      <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
                        Last Activity
                      </div>
                      <div className="font-mono text-xs tabular-nums text-muted-foreground">
                        {formatTimeAgo(getLastActivityDate(deal))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
                      Industry
                    </div>
                    <div className="flex flex-wrap items-center gap-1">
                      {renderIndustry(deal)}
                    </div>
                  </div>

                  <div>
                    <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/80">
                      Source
                    </div>
                    <div className="text-xs text-muted-foreground/80">
                      {deal.overview.source || "—"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
